import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import MailRoundedIcon from '@mui/icons-material/MailRounded';
import SmsRoundedIcon from '@mui/icons-material/SmsRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';

import { triguardBrandProfile } from '../../branding/companyBrand';
import BrandedPreviewShell from '../previews/BrandedPreviewShell';

type AuthChannel = 'phone' | 'email';

const authChannels: Array<{
  description: string;
  icon: React.ElementType;
  id: AuthChannel;
  label: string;
}> = [
  {
    description: 'Text a one-time code',
    icon: SmsRoundedIcon,
    id: 'phone',
    label: 'Phone',
  },
  {
    description: 'Email a one-time code',
    icon: MailRoundedIcon,
    id: 'email',
    label: 'Email',
  },
];

const accountSignals = [
  'Referral links and rewards stay tied to this contact',
  'Payout access stays protected',
  'No password needed for customers',
];

function isValidDestination(channel: AuthChannel, value: string): boolean {
  const trimmedValue = value.trim();

  if (channel === 'email') {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedValue);
  }

  return trimmedValue.replace(/\D/g, '').length >= 10;
}

const ReferralAuthPreview: React.FC = () => {
  const brand = triguardBrandProfile;
  const [channel, setChannel] = useState<AuthChannel>('phone');
  const [destination, setDestination] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);

  const destinationIsValid = useMemo(
    () => isValidDestination(channel, destination),
    [channel, destination]
  );
  const canVerify = codeSent && code.replace(/\D/g, '').length >= 4;

  const handleChannelChange = (nextChannel: AuthChannel) => {
    setChannel(nextChannel);
    setDestination('');
    setCode('');
    setCodeSent(false);
  };

  const handleSendCode = () => {
    if (!destinationIsValid) return;
    setCodeSent(true);
  };

  return (
    <BrandedPreviewShell brand={brand} previewLabel="Referral access" statusLabel="Secure">
      <Stack spacing={2}>
        <Card sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Stack spacing={2}>
            <Box
              sx={{
                alignItems: 'center',
                bgcolor: 'var(--cx-brand-primary-soft)',
                borderRadius: 2,
                color: 'var(--cx-brand-primary-dark)',
                display: 'flex',
                height: 48,
                justifyContent: 'center',
                width: 48,
              }}
            >
              <LockRoundedIcon />
            </Box>

            <Stack spacing={0.75}>
              <Typography
                variant="h4"
                sx={{
                  color: 'var(--cx-brand-text)',
                  fontSize: { xs: 28, sm: 32 },
                  lineHeight: 1.15,
                }}
              >
                Access your referral dashboard
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--cx-brand-text-muted)', lineHeight: 1.6 }}>
                Enter the phone number or email tied to your referrals. We will send a one-time code.
              </Typography>
            </Stack>

            <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              {authChannels.map((option) => {
                const selected = channel === option.id;
                const OptionIcon = option.icon;

                return (
                  <Button
                    aria-pressed={selected}
                    color="inherit"
                    key={option.id}
                    onClick={() => handleChannelChange(option.id)}
                    sx={{
                      alignItems: 'flex-start',
                      bgcolor: selected ? 'var(--cx-brand-primary-soft)' : 'var(--color-gray-100)',
                      border: '1px solid',
                      borderColor: selected ? 'var(--cx-brand-primary)' : 'transparent',
                      color: selected ? 'var(--cx-brand-primary-dark)' : 'var(--cx-brand-text)',
                      flexDirection: 'column',
                      gap: 0.75,
                      minHeight: 96,
                      p: 1.25,
                      textAlign: 'left',
                      '&:hover': {
                        bgcolor: selected ? 'var(--cx-brand-primary-soft)' : 'var(--color-gray-200)',
                        borderColor: 'var(--cx-brand-primary)',
                      },
                    }}
                    type="button"
                    variant="outlined"
                  >
                    <OptionIcon sx={{ fontSize: 22 }} />
                    <Box>
                      <Typography sx={{ color: 'inherit', fontWeight: 900 }}>
                        {option.label}
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          color: selected ? 'var(--cx-brand-primary-dark)' : 'var(--cx-brand-text-muted)',
                          display: 'block',
                          fontSize: 12,
                          fontWeight: 700,
                          lineHeight: 1.35,
                        }}
                      >
                        {option.description}
                      </Typography>
                    </Box>
                  </Button>
                );
              })}
            </Box>

            <TextField
              fullWidth
              label={channel === 'phone' ? 'Mobile phone' : 'Email address'}
              onChange={(event) => {
                setDestination(event.target.value);
                setCode('');
                setCodeSent(false);
              }}
              placeholder={channel === 'phone' ? '(555) 555-0142' : 'name@example.com'}
              slotProps={{
                htmlInput: {
                  inputMode: channel === 'phone' ? 'tel' : 'email',
                },
              }}
              type={channel === 'phone' ? 'tel' : 'email'}
              value={destination}
            />

            <Button
              disabled={!destinationIsValid}
              fullWidth
              onClick={handleSendCode}
              sx={{
                bgcolor: 'var(--cx-brand-primary)',
                color: 'var(--color-base-white)',
                '&:hover': { bgcolor: 'var(--cx-brand-primary-dark)' },
                '&.Mui-disabled': {
                  bgcolor: 'var(--color-gray-200)',
                  color: 'var(--color-gray-500)',
                },
              }}
              type="button"
              variant="contained"
            >
              {codeSent ? 'Send code again' : 'Send code'}
            </Button>
          </Stack>
        </Card>

        <Card sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Stack spacing={2}>
            <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                <ShieldRoundedIcon sx={{ color: 'var(--cx-brand-primary)', fontSize: 22 }} />
                <Typography variant="subtitle2">Verification</Typography>
              </Box>
              <Chip
                label={codeSent ? 'Code sent' : 'Waiting'}
                size="small"
                sx={{
                  bgcolor: codeSent ? 'var(--cx-brand-primary-soft)' : 'var(--color-gray-100)',
                  color: codeSent ? 'var(--cx-brand-primary-dark)' : 'var(--cx-brand-text-muted)',
                  fontWeight: 800,
                }}
              />
            </Box>

            <TextField
              disabled={!codeSent}
              fullWidth
              label="One-time code"
              onChange={(event) => setCode(event.target.value)}
              placeholder="Enter code"
              slotProps={{
                htmlInput: {
                  inputMode: 'numeric',
                  maxLength: 6,
                },
              }}
              value={code}
            />

            <Button
              component={canVerify ? RouterLink : 'button'}
              disabled={!canVerify}
              endIcon={<ArrowForwardRoundedIcon />}
              fullWidth
              sx={{
                bgcolor: 'var(--cx-brand-primary)',
                color: 'var(--color-base-white)',
                '&:hover': { bgcolor: 'var(--cx-brand-primary-dark)' },
                '&.Mui-disabled': {
                  bgcolor: 'var(--color-gray-200)',
                  color: 'var(--color-gray-500)',
                },
              }}
              to={canVerify ? '/preview/referral-dashboard' : undefined}
              type="button"
              variant="contained"
            >
              Open referral dashboard
            </Button>
          </Stack>
        </Card>

        <Card sx={{ p: 2 }}>
          <Stack divider={<Divider flexItem />} spacing={1.5}>
            <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
              <AccountBalanceWalletRoundedIcon sx={{ color: 'var(--cx-brand-primary)', fontSize: 21 }} />
              <Box>
                <Typography variant="body2" sx={{ color: 'var(--cx-brand-text)', fontWeight: 800 }}>
                  Passwordless customer access
                </Typography>
                <Typography variant="caption" sx={{ color: 'var(--cx-brand-text-muted)' }}>
                  Referral dashboard access can be verified by phone or email before exposing rewards.
                </Typography>
              </Box>
            </Stack>

            <Stack spacing={1}>
              {accountSignals.map((signal) => (
                <Box key={signal} sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                  <CheckCircleRoundedIcon sx={{ color: 'var(--cx-brand-primary)', fontSize: 19 }} />
                  <Typography variant="body2" sx={{ color: 'var(--cx-brand-text)' }}>
                    {signal}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Stack>
        </Card>
      </Stack>
    </BrandedPreviewShell>
  );
};

export default ReferralAuthPreview;
